import { useState } from 'react';
import { useAudit, useCreateUser, useUpdateUser, useUsers } from '../api/hooks';
import type { Role } from '../api/types';
import { fmtDateTime } from '../lib/format';

export function AdminUsersPage() {
  const users = useUsers();
  const audit = useAudit();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const [nu, setNu] = useState<{ upn: string; displayName: string; email: string; role: Role }>({
    upn: '',
    displayName: '',
    email: '',
    role: 'quoter',
  });

  return (
    <>
      <h1>Users &amp; Audit</h1>

      <div className="card">
        <h2>Add user</h2>
        {createUser.error && <div className="banner error">{(createUser.error as Error).message}</div>}
        {createUser.isSuccess && <div className="banner ok">User added. They can sign in with that Microsoft 365 account.</div>}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createUser.mutate(
              { upn: nu.upn, displayName: nu.displayName, email: nu.email || undefined, role: nu.role },
              { onSuccess: () => setNu({ upn: '', displayName: '', email: '', role: 'quoter' }) },
            );
          }}
        >
          <div className="row cols-3">
            <div className="field">
              <label>UPN (M365 sign-in) *</label>
              <input required value={nu.upn} onChange={(e) => setNu({ ...nu, upn: e.target.value })} placeholder="rep@tricordit.ca" />
            </div>
            <div className="field">
              <label>Display name *</label>
              <input required value={nu.displayName} onChange={(e) => setNu({ ...nu, displayName: e.target.value })} />
            </div>
            <div className="field">
              <label>Role</label>
              <select value={nu.role} onChange={(e) => setNu({ ...nu, role: e.target.value as Role })}>
                <option value="quoter">Quoter</option>
                <option value="approver">Approver</option>
              </select>
            </div>
          </div>
          <button className="primary" type="submit" disabled={createUser.isPending || !nu.upn.trim() || !nu.displayName.trim()}>
            {createUser.isPending ? 'Adding…' : 'Add user'}
          </button>
        </form>
      </div>

      <div className="card">
        <h2>Users</h2>
        {users.isLoading && <p className="muted">Loading…</p>}
        {users.data && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>UPN</th>
                  <th>Role</th>
                  <th>Active</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.data.map((u) => (
                  <tr key={u.id}>
                    <td>{u.displayName}</td>
                    <td>{u.upn}</td>
                    <td>
                      <span className={`badge role-${u.role}`}>{u.role}</span>
                    </td>
                    <td>{u.isActive ? 'Yes' : 'No'}</td>
                    <td className="btn-row">
                      <button onClick={() => updateUser.mutate({ id: u.id, patch: { isActive: !u.isActive } })}>
                        {u.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button onClick={() => updateUser.mutate({ id: u.id, patch: { role: u.role === 'approver' ? 'quoter' : 'approver' } })}>
                        Make {u.role === 'approver' ? 'quoter' : 'approver'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2>Recent activity</h2>
        {audit.data && audit.data.length === 0 && <p className="muted">No activity yet.</p>}
        {audit.data && audit.data.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Entity</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {audit.data.slice(0, 50).map((a) => (
                  <tr key={a.id}>
                    <td>{fmtDateTime(a.occurredAt)}</td>
                    <td>{a.actorUpn ?? 'system'}</td>
                    <td>{a.action}</td>
                    <td>
                      {a.entityType}
                      {a.entityId ? ` ${a.entityId}` : ''}
                    </td>
                    <td>
                      {a.qboErrorCode ? `[${a.qboErrorCode}] ` : ''}
                      {a.message ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
